import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AlertTriangle, Gauge, IterationCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import {
  checkForEachRules,
  historyBudgetFor,
  type ForEachRegion,
} from '@/pages/workflow/helper/for-each-region';
import {
  defaultCollectPort,
  describeForEachSentence,
  forEachEntryCandidates,
  forEachExitCandidates,
  forEachListSource,
  forEachSentenceFromGraph,
  type ForEachPickerOption,
  type ForEachSentence,
} from '@/pages/workflow/helper/for-each-author';
import { cn } from '@/lib/utils';

interface ForEachSentenceSectionProps {
  nodeId: string;
  disabled?: boolean;
  /** Back to a single step per item (shown when the body is not yet drawn). */
  onCancel?: () => void;
}

const hint = 'text-xs text-muted-foreground';

/** Labels for a Select; duplicates get a counter so every option is unique. */
const labelled = (options: ForEachPickerOption[]) => {
  const seen = new Map<string, number>();
  return options.map((option) => {
    const count = (seen.get(option.label) ?? 0) + 1;
    seen.set(option.label, count);
    return {
      ...option,
      text: count === 1 ? option.label : `${option.label} (${count})`,
    };
  });
};

/**
 * The For-each body as one sentence: For each [item] in [list], run [this
 * part of the canvas], collecting [the last step's output]. Each picker
 * draws or moves the matching canvas edge; the rules are checked live.
 */
export function ForEachSentenceSection({
  nodeId,
  disabled = false,
  onCancel,
}: ForEachSentenceSectionProps) {
  const { nodes, edges, groupedNodes, applyForEachSentence, clearForEachBody } =
    useNodeStore(
      useShallow((state) => ({
        nodes: state.nodes,
        edges: state.edges,
        groupedNodes: state.groupedNodes,
        applyForEachSentence: state.applyForEachSentence,
        clearForEachBody: state.clearForEachBody,
      })),
    );
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const { sentence, region } = useMemo(
    () => forEachSentenceFromGraph(nodes, edges, nodeId),
    [nodes, edges, nodeId],
  );
  const list = forEachListSource(nodes, edges, nodeId);
  const listLabel = list
    ? `${list.name}${list.port ? ` · ${list.port}` : ''}`
    : null;
  const entries = labelled(forEachEntryCandidates(nodes, nodeId, catalog));
  const exits = labelled(
    forEachExitCandidates(nodes, edges, nodeId, sentence.entryId, catalog),
  );
  const exitNode = nodes.find((node) => node.id === sentence.exitId);
  const ports = exitNode?.data?.struct?.output_ports ?? [];
  const problems = useMemo(
    () =>
      region
        ? checkForEachRules(nodes, edges, region, catalog)
        : ([] as never[]),
    [nodes, edges, region, catalog],
  );
  const budget =
    region?.regionMode && region.regionIds.length
      ? historyBudgetFor(region as ForEachRegion, region.carrier.data?.struct)
      : null;
  const [itemName, setItemName] = useState(sentence.itemName);
  useEffect(() => setItemName(sentence.itemName), [sentence.itemName]);

  const apply = (patch: Partial<ForEachSentence>) =>
    applyForEachSentence(nodeId, { ...sentence, ...patch });
  const hasBody = Boolean(
    region && (region.itemEdges.length || region.collectEdges.length),
  );

  return (
    <section className='space-y-2' data-testid='for-each-section'>
      <Label className='flex items-center gap-1.5 text-sm'>
        <IterationCw className='size-3.5 text-teal-600 dark:text-teal-300' />
        For each item, run this part of the canvas
      </Label>
      <p
        className='rounded-md border border-teal-200 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10 px-2 py-1.5 text-xs text-teal-900 dark:text-teal-100'
        data-testid='for-each-sentence'
      >
        {describeForEachSentence(sentence, nodes, listLabel, region)}
      </p>

      <div className='grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5 text-xs'>
        <span>Call each</span>
        <Input
          value={itemName}
          disabled={disabled}
          className='h-7 text-xs'
          aria-label='Item name'
          data-testid='for-each-item-name'
          onChange={(event) => setItemName(event.target.value)}
          onBlur={() => {
            const next = itemName.trim() || 'item';
            if (next !== sentence.itemName) apply({ itemName: next });
          }}
        />
        <span>in</span>
        <span
          className={cn('truncate', !listLabel && 'text-muted-foreground')}
          data-testid='for-each-list'
        >
          {listLabel ?? 'connect a list to Items'}
        </span>
        <span>First step</span>
        <Select
          options={entries.map((option) => option.text)}
          selected={
            entries.find((option) => option.id === sentence.entryId)?.text
          }
          placeholder={
            entries.length ? 'choose a step…' : 'add a step to the canvas'
          }
          allowAllOption={false}
          disabled={disabled || entries.length === 0}
          className='h-7 w-full min-w-0 text-xs'
          aria-label='First step'
          onSelect={(text) => {
            const picked = entries.find((option) => option.text === text);
            if (picked) apply({ entryId: picked.id });
          }}
        />
        <span>Last step</span>
        <Select
          options={exits.map((option) => option.text)}
          selected={exits.find((option) => option.id === sentence.exitId)?.text}
          placeholder={
            sentence.entryId ? 'choose a step…' : 'pick the first step first'
          }
          allowAllOption={false}
          disabled={disabled || !sentence.entryId || exits.length === 0}
          className='h-7 w-full min-w-0 text-xs'
          aria-label='Last step'
          onSelect={(text) => {
            const picked = exits.find((option) => option.text === text);
            if (!picked) return;
            const exit = nodes.find((node) => node.id === picked.id);
            apply({
              exitId: picked.id,
              collectPortId: defaultCollectPort(exit),
            });
          }}
        />
        {sentence.exitId && ports.length > 1 ? (
          <>
            <span>Collect</span>
            <Select
              options={ports.map((port) => port.display_name || port.name)}
              selected={(() => {
                const port = ports.find(
                  (candidate) => candidate.id === sentence.collectPortId,
                );
                return port ? port.display_name || port.name : undefined;
              })()}
              allowAllOption={false}
              disabled={disabled}
              className='h-7 w-full min-w-0 text-xs'
              aria-label='Collected output'
              onSelect={(text) => {
                const port = ports.find(
                  (candidate) =>
                    (candidate.display_name || candidate.name) === text,
                );
                if (port) apply({ collectPortId: port.id });
              }}
            />
          </>
        ) : null}
      </div>
      <p className={hint}>
        Or draw it: drag <strong>each item</strong> from this node to the first
        step, then drag the last step’s output to <strong>collect</strong>.
      </p>

      {budget ? (
        <p
          className='flex items-center gap-1.5 text-xs'
          data-testid='for-each-budget'
        >
          <Gauge className='size-3.5 text-muted-foreground' />
          {budget.sentence}
        </p>
      ) : null}

      {problems.length ? (
        <ul
          className='space-y-1 rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs text-amber-900 dark:text-amber-200'
          data-testid='for-each-problems'
        >
          {problems.map((problem) => (
            <li
              key={`${problem.code}-${problem.nodeIds.join(',')}`}
              className='flex gap-1.5'
              data-testid={`for-each-problem-${problem.code}`}
              data-severity={problem.severity}
            >
              <AlertTriangle className='mt-0.5 size-3 shrink-0' />
              <span>
                {problem.message}
                {problem.hint ? (
                  <span className='block text-amber-800/80 dark:text-amber-200/80'>
                    {problem.hint}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className='flex gap-2'>
        {hasBody ? (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 text-xs'
            disabled={disabled}
            data-testid='for-each-remove'
            onClick={() => clearForEachBody(nodeId)}
          >
            Run a single step instead
          </Button>
        ) : onCancel ? (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 text-xs'
            data-testid='for-each-cancel'
            onClick={onCancel}
          >
            Back to a single step
          </Button>
        ) : null}
      </div>
    </section>
  );
}
